import React from 'react';
import { useIntl } from 'react-intl';

import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';

import { GRID_FILTER_BUTTONS } from 'components/AgGrid/helpers';
import ConditionDatePicker from './components/ConditionDatePicker';
import { AgDateCondition } from './types';

type Props = {
  format: string;
  buttons: string[];

  operator: 'and' | 'or';
  setOperator: (value: 'and' | 'or') => void;

  primaryCondition: AgDateCondition;
  secondaryCondition: AgDateCondition;

  handleAccept: () => void;
  handleReset?: () => void;
  closeFilter?: () => void;
};

const AgDateFilterLayout: React.FC<Props> = ({
  format,
  buttons,

  operator,
  setOperator,

  primaryCondition,
  secondaryCondition,

  handleAccept,
  handleReset,
  closeFilter,
}) => {
  const { formatMessage } = useIntl();
  return (
    <Box sx={{ minWidth: 284, maxWidth: '100%' }}>
      <div className="ag-filter-body-wrapper ag-simple-filter-body-wrapper">
        <ConditionDatePicker format={format} {...primaryCondition} />

        {!!primaryCondition.dateFrom && (
          <>
            <RadioGroup
              row
              value={operator}
              onChange={(e) => setOperator(e.target.value as 'and' | 'or')}
              sx={{ mx: 'auto' }}
            >
              <FormControlLabel
                value="or"
                control={<Radio size="small" />}
                slotProps={{ typography: { fontSize: 14 } }}
                label={formatMessage({
                  id: 'company.workspace.grid.filters.operator.or',
                  defaultMessage: 'OR',
                })}
              />
              <FormControlLabel
                value="and"
                control={<Radio size="small" />}
                slotProps={{ typography: { fontSize: 14 } }}
                label={formatMessage({
                  id: 'company.workspace.grid.filters.operator.and',
                  defaultMessage: 'AND',
                })}
              />
            </RadioGroup>

            <ConditionDatePicker format={format} {...secondaryCondition} />
          </>
        )}
      </div>
      <div className="ag-filter-apply-panel">
        {buttons.includes(GRID_FILTER_BUTTONS.APPLY) && (
          <button
            type="button"
            className="ag-button ag-standard-button ag-filter-apply-panel-button"
            onClick={handleAccept}
          >
            {formatMessage({ id: 'button.ok', defaultMessage: 'OK' })}
          </button>
        )}

        {buttons.includes(GRID_FILTER_BUTTONS.CANCEL) && (
          <button
            type="button"
            className="ag-button ag-standard-button ag-filter-cancel-panel-button"
            onClick={closeFilter}
          >
            {formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
          </button>
        )}

        {buttons.includes(GRID_FILTER_BUTTONS.RESET) && (
          <button
            type="button"
            className="ag-button ag-standard-button ag-filter-reset-panel-button"
            onClick={handleReset}
          >
            {formatMessage({ id: 'button.reset', defaultMessage: 'Reset' })}
          </button>
        )}
      </div>
    </Box>
  );
};

AgDateFilterLayout.displayName = 'AgDateFilter';

export default React.memo(AgDateFilterLayout);
